/** One price band of the proxy-bid increment table (HLD AUC-02). */
export interface BidIncrementBandState {
  /** Exclusive upper bound of the band; `null` on the open-ended top band. */
  maxPrice: number | null;
  increment: number;
}

/**
 * Platform-wide commerce policy (HLD ADM-02). The Admin Portal owns these and
 * they are per-tenant overridable, so the storefront reads them through a
 * repository rather than hardcoding them in source.
 */
export interface PlatformConfigState {
  /** AUC-05 anti-snipe: a bid inside this window pushes the auction end out. */
  antiSnipeWindowMs: number;
  /** AUC-02 increment bands, ascending by `maxPrice`. */
  bidIncrements: BidIncrementBandState[];
  /** BUY-04 checkout-initiated reservation hold (HLD §8.1, D-04). */
  reservationWindowMs: number;
  /** PKP-04 fallback pickup hold for a store that sets none. */
  pickupHoldDays: number;
  /** CAT-05 Local Finds radius choices, in miles. */
  localFindsRadiiMi: number[];
  defaultRadiusMi: number;
  /** PAY-04 combined sales-tax rate applied to items plus shipping. */
  salesTaxRate: number;
}

/**
 * Resolves the AUC-02 increment for a current price. Bands are exclusive on
 * `maxPrice`, so $25.00 takes the $25-$99.99 band, not the one below it.
 */
export function resolveBidIncrement(bands: BidIncrementBandState[], currentPrice: number): number {
  const band = bands.find(b => b.maxPrice === null || currentPrice < b.maxPrice);
  return band?.increment ?? bands[bands.length - 1].increment;
}
